/** The admin panel: generate time-limited access links (+ QR), and view/revoke existing links. */
import { useState, type FormEvent } from "react";
import { ChevronDown } from "lucide-react";
import {
  linkNeverExpires,
  useCreateLink,
  useLinks,
  usePlayerRun,
  usePlayerRuns,
  useRevokeLink,
  type CreatedLink,
} from "../../api/admin";
import { PageShell } from "../PageShell";
import { Button, FormError, TextField, apiErrorMessage } from "../../components/ui";
import { RunTranscript } from "../../components/RunTranscript";
import { cx } from "../../lib/ui";
import { gameContent } from "../../content";
import type { TranscriptEntry } from "../../game/machine";
import type { Scores } from "../../game/scoring";

const SHORT: Record<string, string> = {
  dealSecurity: "Deal",
  clientSatisfaction: "Client",
  counterpartyCooperation: "Counterparty",
  transactionProcess: "Process",
};
const endingLabel = (id: string | null) => gameContent.endings.find((e) => e.id === id)?.label ?? "—";

export function AdminPanel() {
  const links = useLinks(true);
  const playerRuns = usePlayerRuns(true);
  const createLink = useCreateLink();
  const revoke = useRevokeLink();
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const openRun = usePlayerRun(openRunId);

  const [label, setLabel] = useState("");
  const [hours, setHours] = useState(24);
  const [maxUses, setMaxUses] = useState("");
  const [created, setCreated] = useState<CreatedLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const c = await createLink.mutateAsync({
        label,
        expiresInHours: hours === 0 ? null : hours, // 0 = "No expiry"
        maxUses: maxUses ? Number(maxUses) : undefined,
      });
      setCreated(c);
      setCopied(false);
      setLabel("");
      setMaxUses("");
    } catch {
      /* error rendered below */
    }
  }

  return (
    <PageShell>
      <h1 className="font-display text-fluid-xl font-semibold text-ink">Access links</h1>
      <p className="mt-1 text-ink-600">
        Generate time-limited links (and QR codes) for players to register or sign in.
      </p>

      <form
        onSubmit={submit}
        className="mt-6 grid gap-4 rounded-sm border border-ink-150 bg-white p-5 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end"
      >
        <TextField label="Label" name="label" required value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Summer trainees 2026" />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="hours">Valid for</label>
          <select
            id="hours"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="rounded-sm border border-ink-300 bg-white px-3 py-2 text-ink focus-visible:shadow-[var(--shadow-focus)]"
          >
            <option value={1}>1 hour</option>
            <option value={2}>2 hours</option>
            <option value={4}>4 hours</option>
            <option value={8}>8 hours</option>
            <option value={24}>1 day</option>
            <option value={72}>3 days</option>
            <option value={168}>7 days</option>
            <option value={720}>30 days</option>
            <option value={0}>No expiry</option>
          </select>
        </div>
        <TextField label="Max uses (optional)" name="maxUses" type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="∞" />
        <Button type="submit" disabled={createLink.isPending}>{createLink.isPending ? "Generating…" : "Generate"}</Button>
      </form>
      {createLink.error && <div className="mt-3"><FormError>{apiErrorMessage(createLink.error)}</FormError></div>}

      {created && (
        <div className="mt-4 flex flex-col gap-4 rounded-sm border border-[var(--color-tier-best)] bg-[color-mix(in_oklab,var(--success)_8%,white)] p-4 sm:flex-row sm:items-center">
          <img src={created.qrDataUrl} alt="QR code for the access link" className="h-32 w-32 shrink-0 rounded-sm bg-white p-1" />
          <div className="min-w-0">
            <p className="font-medium text-ink">Link created — copy it now; it won't be shown again.</p>
            <code className="mt-1 block truncate rounded-sm bg-white px-2 py-1 font-mono text-sm text-ink-700">{created.url}</code>
            <div className="mt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard?.writeText(created.url);
                  setCopied(true);
                }}
              >
                {copied ? "Copied" : "Copy link"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <h2 className="mt-10 font-display text-fluid-lg font-semibold text-ink">All links</h2>
      <div className="mt-3 overflow-x-auto rounded-sm border border-ink-150">
        <table className="w-full text-sm">
          <thead className="bg-ink-100 text-left text-ink-600">
            <tr>
              <th className="px-3 py-2 font-medium">Label</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Expires</th>
              <th className="px-3 py-2 font-medium">Uses</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {links.data?.map((l) => (
              <tr key={l.id} className="border-t border-ink-150">
                <td className="px-3 py-2 text-ink">{l.label}</td>
                <td className="px-3 py-2">
                  <span
                    className={cx(
                      "font-medium capitalize",
                      l.status === "active"
                        ? "text-[var(--success)]"
                        : l.status === "revoked"
                          ? "text-[var(--danger)]"
                          : "text-ink-500",
                    )}
                  >
                    {l.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-ink-600">
                  {linkNeverExpires(l.expiresAt) ? "Never" : new Date(l.expiresAt).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-ink-600">
                  {l.usedCount}
                  {l.maxUses ? ` / ${l.maxUses}` : ""}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {l.status === "active" && l.url && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            void navigator.clipboard?.writeText(l.url!);
                            setCopiedLinkId(l.id);
                          }}
                          className="text-sm font-medium text-ink-600 hover:text-ink"
                        >
                          {copiedLinkId === l.id ? "Copied" : "Copy"}
                        </button>
                        <a
                          href={`/admin/share/${l.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-ink-600 hover:text-ink"
                        >
                          Share
                        </a>
                      </>
                    )}
                    {l.status === "active" && (
                      <button onClick={() => revoke.mutate(l.id)} className="text-sm text-brand hover:underline">
                        Revoke
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {links.data?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-ink-500">No links yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 font-display text-fluid-lg font-semibold text-ink">Player results</h2>
      <p className="mt-1 text-sm text-ink-600">Completed games across all players.</p>
      <div className="mt-3 overflow-x-auto rounded-sm border border-ink-150">
        <table className="w-full text-sm">
          <thead className="bg-ink-100 text-left text-ink-600">
            <tr>
              <th className="px-3 py-2 font-medium">Player</th>
              <th className="px-3 py-2 font-medium">Outcome</th>
              <th className="px-3 py-2 font-medium">Mode</th>
              <th className="px-3 py-2 font-medium">Finished</th>
              <th className="px-3 py-2 font-medium">Scores</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {playerRuns.data?.map((r) => {
              const open = openRunId === r.id;
              return (
                <tr
                  key={r.id}
                  onClick={() => setOpenRunId(open ? null : r.id)}
                  className={cx(
                    "cursor-pointer border-t border-ink-150 align-top hover:bg-ink-100",
                    open && "bg-ink-100",
                  )}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-ink">{r.player?.displayName || "—"}</div>
                    <div className="text-xs text-ink-500">{r.player?.email}</div>
                  </td>
                  <td className="px-3 py-2 text-ink-700">{endingLabel(r.endingId)}</td>
                  <td className="px-3 py-2 text-ink-600">{r.mode === "untimed" ? "Practice" : "Timed"}</td>
                  <td className="px-3 py-2 text-ink-600">{r.finishedAt ? new Date(r.finishedAt).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-ink-600">
                      {gameContent.meta.dimensions.map((d) => (
                        <span key={d.id} className="tabular-nums">
                          {SHORT[d.id] ?? d.label} <span className="font-mono text-ink">{r.scores?.[d.id] ?? "—"}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenRunId(open ? null : r.id);
                      }}
                      aria-expanded={open}
                      className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-brand hover:underline"
                    >
                      {open ? "Hide" : "Review"}
                      <ChevronDown
                        size={16}
                        className={cx("transition-transform", open && "rotate-180")}
                        aria-hidden="true"
                      />
                    </button>
                  </td>
                </tr>
              );
            })}
            {playerRuns.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-ink-500">No completed games yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openRunId && (
        <section
          className="mt-4 rounded-sm border border-ink-300 bg-white p-4 sm:p-5"
          aria-label="Answer-by-answer review"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-display text-fluid-lg font-semibold text-ink">Answer-by-answer review</h3>
              {openRun.data?.player && (
                <p className="truncate text-sm text-ink-600">
                  {openRun.data.player.displayName || "—"} · {openRun.data.player.email} ·{" "}
                  {endingLabel(openRun.data.endingId)}
                </p>
              )}
            </div>
            <Button variant="secondary" onClick={() => setOpenRunId(null)}>
              Close
            </Button>
          </div>
          <div className="mt-4">
            {openRun.isLoading && <p className="text-ink-500">Loading…</p>}
            {openRun.error && <FormError>{apiErrorMessage(openRun.error)}</FormError>}
            {openRun.data && (
              <RunTranscript
                transcript={
                  (Array.isArray(openRun.data.transcript) ? openRun.data.transcript : []) as TranscriptEntry[]
                }
                scores={openRun.data.scores as Scores | null}
              />
            )}
          </div>
        </section>
      )}
    </PageShell>
  );
}
